import { useCallback } from 'react';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Module-level singleton loader: the reCAPTCHA script should only ever be injected once
// per page lifetime, and every caller (however many login attempts happen in this tab)
// must share the SAME loading promise instead of racing each other.
//
// The previous version re-ran `document.querySelector(...)` on every call: if it found the
// <script> tag already in the DOM (e.g. appended by an earlier, still in-flight call — the
// script fetch takes real time) but `window.grecaptcha` wasn't defined YET, it just gave up
// and resolved null immediately instead of waiting for that in-flight load to finish. That's
// exactly a "first attempt silently fails, second attempt works" bug: the first call is still
// loading the script when the user submits, so it (or a second, overlapping call) bails out
// early; by the next attempt the script has since finished loading in the background and the
// "already loaded" branch happens to succeed.
let scriptLoadPromise: Promise<void> | null = null;

function isGrecaptchaReady(): boolean {
    return typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.ready === 'function';
}

function loadRecaptchaScript(): Promise<void> {
    if (isGrecaptchaReady()) {
        return Promise.resolve();
    }
    if (scriptLoadPromise) {
        return scriptLoadPromise;
    }

    scriptLoadPromise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>('script[src*="google.com/recaptcha"]');
        if (existing) {
            // Someone else (an earlier call) already added it — wait for THAT element's own
            // load/error, don't assume it's already finished just because it's present.
            if (isGrecaptchaReady()) {
                resolve();
                return;
            }
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script')));
            return;
        }

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
        document.head.appendChild(script);
    }).catch((error) => {
        // Don't cache a failed load — let the next attempt retry instead of being
        // permanently stuck resolving to the same rejected promise forever.
        scriptLoadPromise = null;
        throw error;
    });

    return scriptLoadPromise;
}

export const useRecaptcha = () => {
  const executeRecaptcha = useCallback(async (action: string = 'login'): Promise<string | null> => {
    if (!RECAPTCHA_SITE_KEY) {
      console.error('reCAPTCHA site key is not configured');
      return null;
    }

    try {
      await loadRecaptchaScript();
    } catch (error) {
      console.error('Failed to load reCAPTCHA script:', error);
      return null;
    }

    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(RECAPTCHA_SITE_KEY!, { action })
          .then((token: string) => resolve(token))
          .catch((error: any) => {
            console.error('reCAPTCHA execution failed:', error);
            resolve(null);
          });
      });
    });
  }, []);

  return { executeRecaptcha };
};

// Extend the Window interface to include grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}
